/**
 * Sync Service - Auto-sync pending data with server
 * Runs every 5 minutes when online
 */

import axios from 'axios';
import StorageService from './storageService';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

class SyncService {
  constructor() {
    this.syncInterval = null;
    this.isSyncing = false;
    this.syncIntervalMinutes = 5;
  }

  /**
   * Start auto-sync (every 5 minutes)
   */
  startAutoSync() {
    if (this.syncInterval) {
      console.log('[Sync] Auto-sync already running');
      return;
    }

    console.log(`[Sync] Starting auto-sync (every ${this.syncIntervalMinutes} minutes)`);
    
    // Run immediately on start
    this.syncAll();
    
    // Then run every 5 minutes
    this.syncInterval = setInterval(() => {
      this.syncAll();
    }, this.syncIntervalMinutes * 60 * 1000);
  }

  /**
   * Stop auto-sync
   */
  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
      console.log('[Sync] Auto-sync stopped');
    }
  }

  /**
   * Main sync function - syncs all pending data
   */
  async syncAll() {
    // Don't sync if offline or already syncing
    if (!navigator.onLine) {
      console.log('[Sync] Offline - skipping sync');
      return { success: false, reason: 'offline' };
    }

    if (this.isSyncing) {
      console.log('[Sync] Sync already in progress');
      return { success: false, reason: 'already_syncing' };
    }

    this.isSyncing = true;
    console.log('[Sync] Starting sync...');

    try {
      const results = {
        orders: { synced: 0, failed: 0 },
        ledger: { synced: 0, failed: 0 },
        total: 0
      };

      // Sync pending orders
      const orderResult = await this.syncPendingOrders();
      results.orders = orderResult;

      // Sync pending ledger transactions
      const ledgerResult = await this.syncPendingLedgerTransactions();
      results.ledger = ledgerResult;

      // Calculate total
      results.total = results.orders.synced + results.ledger.synced;

      // Update last sync time
      if (results.total > 0) {
        await StorageService.setLastSyncTime();
        console.log(`[Sync] Complete: ${results.total} items synced`);
        
        // Show success notification
        toast.success(`Synced ${results.total} item${results.total > 1 ? 's' : ''}`, {
          description: `${results.orders.synced} order${results.orders.synced !== 1 ? 's' : ''}, ${results.ledger.synced} ledger transaction${results.ledger.synced !== 1 ? 's' : ''}`,
          duration: 3000
        });
      } else {
        console.log('[Sync] Nothing to sync');
      }

      return { success: true, results };

    } catch (error) {
      console.error('[Sync] Error during sync:', error);
      toast.error('Sync failed', {
        description: 'Will retry in 5 minutes',
        duration: 3000
      });
      return { success: false, error: error.message };
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Sync pending orders
   */
  async syncPendingOrders() {
    const result = { synced: 0, failed: 0, errors: [] };

    try {
      const pendingOrders = await StorageService.getPendingOrders();
      
      if (pendingOrders.length === 0) {
        return result;
      }

      console.log(`[Sync] Syncing ${pendingOrders.length} pending order(s)`);

      for (const order of pendingOrders) {
        try {
          // Prepare order data for API
          const orderDetails = JSON.parse(order.order_details_json);
          const orderData = {
            franchise_id: order.franchise_id,
            items: orderDetails.items.map(item => ({
              menu_item_id: item.menu_item_id,
              item_name_snapshot: item.name,
              item_price_snapshot: item.price,
              quantity: item.quantity
            })),
            ledger: order.ledger || 'cash'
          };

          // Upload to server
          const response = await axios.post(`${API}/api/orders`, orderData);

          if (response.data.success) {
            const serverOrderNo = response.data.order_no;
            
            // Update order with server ID and save to synced orders
            const syncedOrder = {
              ...order,
              order_no: serverOrderNo,
              status: 'synced',
              synced_at: new Date().toISOString()
            };

            await StorageService.saveSyncedOrder(syncedOrder);
            
            // Remove from pending queue
            await StorageService.deletePendingOrder(order.order_no);
            
            result.synced++;
            console.log(`[Sync] Order synced: ${order.order_no} → ${serverOrderNo}`);
          }
        } catch (error) {
          console.error(`[Sync] Failed to sync order ${order.order_no}:`, error);
          result.failed++;
          result.errors.push({
            order_no: order.order_no,
            error: error.message
          });
        }
      }

    } catch (error) {
      console.error('[Sync] Error syncing orders:', error);
    }

    return result;
  }

  /**
   * Sync pending ledger transactions
   */
  async syncPendingLedgerTransactions() {
    const result = { synced: 0, failed: 0, errors: [] };

    try {
      const pendingTransactions = await StorageService.getPendingLedgerTransactions();
      
      if (pendingTransactions.length === 0) {
        return result;
      }

      console.log(`[Sync] Syncing ${pendingTransactions.length} pending ledger transaction(s)`);

      for (const transaction of pendingTransactions) {
        try {
          // Upload to server based on transaction type
          let response;
          
          if (transaction.type === 'add_money') {
            response = await axios.post(
              `${API}/api/ledger/parties/${transaction.party_id}/add-money`,
              {
                amount: transaction.amount,
                description: transaction.description || 'Money added'
              }
            );
          } else {
            // Handle other transaction types if needed
            console.warn(`[Sync] Unknown transaction type: ${transaction.type}`);
            continue;
          }

          if (response.data.success) {
            // Remove from pending queue
            await StorageService.deletePendingLedgerTransaction(transaction.transaction_id);
            
            // Update local party balance if available
            const party = await StorageService.get('ledger_parties', transaction.party_id);
            if (party) {
              party.balance = response.data.new_balance || party.balance;
              await StorageService.updateLedgerParty(party);
            }
            
            result.synced++;
            console.log(`[Sync] Ledger transaction synced: ${transaction.transaction_id}`);
          }
        } catch (error) {
          console.error(`[Sync] Failed to sync transaction ${transaction.transaction_id}:`, error);
          result.failed++;
          result.errors.push({
            transaction_id: transaction.transaction_id,
            error: error.message
          });
        }
      }

    } catch (error) {
      console.error('[Sync] Error syncing ledger transactions:', error);
    }

    return result;
  }

  /**
   * Force sync now (manual trigger)
   */
  async forceSyncNow() {
    console.log('[Sync] Force sync triggered');
    
    if (!navigator.onLine) {
      toast.error('Cannot sync while offline', {
        description: 'Please connect to internet first',
        duration: 3000
      });
      return { success: false, reason: 'offline' };
    }

    toast.info('Syncing...', { duration: 1000 });
    return await this.syncAll();
  }

  /**
   * Get sync status
   */
  async getSyncStatus() {
    const stats = await StorageService.getSyncStats();
    return {
      isSyncing: this.isSyncing,
      isAutoSyncRunning: this.syncInterval !== null,
      ...stats
    };
  }
}

// Export singleton instance
const syncService = new SyncService();
export default syncService;
